import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
} from "@reduxjs/toolkit/query/react";
import { getUserFromLS } from "../../utils/getUserFromLS";
import {
  getTutorialFixtureEpoch,
  isTutorialFixtureActive,
} from "../../utils/tutorialFixtureState";
import type {
  ApiError,
  BookmarkState,
  BookmarkWrite,
  Capabilities,
  Competitor,
  Draft,
  DraftCreate,
  DraftPatch,
  ExploreEntry,
  FrozenInsight,
  GenerationRequest,
  InsightItem,
  Overview,
  Run,
  ReviewState,
  ReviewWrite,
  SourcePage,
  Subject,
  Trend,
} from "./contracts.generated";
export type Scope = {
  clientId: string;
  actorId: string;
  preview?: boolean;
  tutorial?: number;
};
export type Page<T> = {
  items: T[];
  next_cursor?: string | null;
  snapshot_at: string;
  total?: number | null;
};
type Query = Scope & { query?: string };
/** First pages stay current; cursor pages retain their server snapshot. */
export const liveListOptions = (cursor?: string | null) => ({
  refetchOnMountOrArgChange: true,
  refetchOnFocus: !cursor,
  refetchOnReconnect: !cursor,
});
export const isAccessError = (error: unknown) =>
  !!error &&
  typeof error === "object" &&
  "status" in error &&
  [401, 403].includes(Number(error.status));
type Entity = Scope & { id: string; query?: string };
type Request = Scope & {
  path: string;
  method?: string;
  body?: unknown;
  key?: string;
};
export type RequestError = ApiError & { status?: number | string };
const transport = fetchBaseQuery({ baseUrl: "", timeout: 30000 });
let tutorialSession:
  | {
      epoch: number;
      respond: (request: globalThis.Request) => Promise<Response>;
    }
  | undefined;
const baseQuery: BaseQueryFn<Request, unknown, RequestError> = async (
  args,
  api,
  extra,
) => {
  if (args.tutorial != null) {
    if (
      !isTutorialFixtureActive() ||
      args.tutorial !== getTutorialFixtureEpoch()
    )
      return {
        error: {
          code: "session_changed",
          message: "The guided tour has ended.",
          status: 401,
        },
      };
    const { createFixtureResponder } = await import("./fixtures/browser");
    if (
      !isTutorialFixtureActive() ||
      args.tutorial !== getTutorialFixtureEpoch()
    )
      return {
        error: {
          code: "session_changed",
          message: "The guided tour has ended.",
          status: 401,
        },
      };
    if (tutorialSession?.epoch !== args.tutorial)
      tutorialSession = {
        epoch: args.tutorial,
        respond: createFixtureResponder("populated"),
      };
    const response = await tutorialSession.respond(
      new Request(
        `${window.location.origin}/clients/${args.clientId}/insights${args.path}`,
        {
          method: args.method ?? "GET",
          ...(args.body
            ? {
                body: JSON.stringify(args.body),
                headers: { "Content-Type": "application/json" },
              }
            : {}),
        },
      ),
    );
    const data = await response.json();
    return response.ok
      ? { data }
      : { error: { ...data, status: response.status } };
  }
  if (!args.preview && isTutorialFixtureActive())
    return {
      error: {
        code: "session_changed",
        message: "Live changes are unavailable during the guided tour.",
        status: 401,
      },
    };
  const user = getUserFromLS();
  const auth = (
    api.getState() as {
      auth?: { isAuthenticated?: boolean; user?: { id?: string } };
    }
  ).auth;
  if (
    !args.preview &&
    (auth?.isAuthenticated === false ||
      !user?.access_token ||
      user.id !== args.actorId ||
      (auth?.user?.id && auth.user.id !== args.actorId))
  )
    return {
      error: {
        code: "session_changed",
        message: "Your session changed. Sign in again to continue.",
        status: 401,
      },
    };
  const prefix = args.preview
    ? `${window.location.origin}/__insights_fixture__`
    : import.meta.env.VITE_API_BASE_URL;
  if (import.meta.env.DEV && import.meta.env.MODE !== "test" && args.preview) {
    const { previewResponse } = await import("./fixtures/browser");
    const response = await previewResponse(
      new Request(`${prefix}/clients/${args.clientId}/insights${args.path}`, {
        method: args.method ?? "GET",
        ...(args.body
          ? {
              body: JSON.stringify(args.body),
              headers: { "Content-Type": "application/json" },
            }
          : {}),
      }),
    );
    const data = await response.json();
    return response.ok
      ? { data }
      : { error: { ...data, status: response.status } };
  }
  const result = await transport(
    {
      url: `${prefix}/clients/${encodeURIComponent(args.clientId)}/insights${args.path}`,
      method: args.method ?? "GET",
      body: args.body,
      headers: {
        ...(!args.preview
          ? { Authorization: `Bearer ${user.access_token}` }
          : {}),
        ...(args.key ? { "Idempotency-Key": args.key } : {}),
      },
    },
    api,
    extra,
  );
  if (result.error) {
    const data = result.error.data as Partial<ApiError> | undefined;
    return {
      error: {
        code: data?.code ?? "request_failed",
        message:
          data?.message ??
          (result.error.status === 403
            ? "You do not have access to this workspace."
            : result.error.status === 404
              ? "This item is no longer available."
              : "We could not complete this request. Please try again."),
        field_errors: data?.field_errors,
        retryable: data?.retryable,
        status: result.error.status,
      },
    };
  }
  return { data: result.data };
};
const path = (value: string, query?: string) =>
  `${value}${query ? `?${query.replace(/^\?/, "")}` : ""}`;
const tag = (scope: Scope) =>
  `${scope.clientId}:${scope.actorId}:${scope.tutorial ?? "live"}:${scope.preview ?? false}`;
export const insightJobTags = (scope: Scope) => [
  { type: "Workspace" as const, id: tag(scope) },
  { type: "Items" as const, id: tag(scope) },
];
const pageTags = (type: "Workspace" | "Items", scope: Query) =>
  new URLSearchParams(scope.query).has("cursor") ? [] : [{ type, id: tag(scope) }];
export const mutationKey = () => crypto.randomUUID();
export function errorMessage(error: unknown) {
  if (
    error &&
    typeof error === "object" &&
    "field_errors" in error &&
    error.field_errors &&
    typeof error.field_errors === "object"
  ) {
    const fields = Object.values(error.field_errors).filter(
      (value): value is string => typeof value === "string",
    );
    if (fields.length) return fields.join(" ");
  }
  return error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
    ? error.message
    : error
      ? "We could not complete this request. Please try again."
      : null;
}
export const insightsApi = createApi({
  reducerPath: "insightsApi",
  baseQuery,
  tagTypes: ["Workspace", "Items", "Draft"],
  keepUnusedDataFor: 30,
  endpoints: (b) => ({
    overview: b.query<Overview, Scope>({
      query: (s) => ({ ...s, path: "/overview" }),
      providesTags: (_, __, s) => [{ type: "Workspace", id: tag(s) }],
    }),
    explore: b.query<Page<ExploreEntry>, Query>({
      query: (s) => ({ ...s, path: path("/explore", s.query) }),
      providesTags: (_, __, s) => pageTags("Workspace", s),
    }),
    items: b.query<Page<InsightItem>, Query & { collection?: "saved" }>({
      query: (s) => ({
        ...s,
        path: path(s.collection === "saved" ? "/saved" : "/items", s.query),
      }),
      providesTags: (_, __, s) => pageTags("Items", s),
    }),
    item: b.query<InsightItem, Entity & { revision?: number }>({
      query: (s) => ({ ...s, path: `/items/${encodeURIComponent(s.id)}` }),
      providesTags: (_, __, s) => [{ type: "Items", id: tag(s) }],
    }),
    resolve: b.query<InsightItem, Query>({
      query: (s) => ({ ...s, path: path("/items/resolve", s.query) }),
    }),
    trend: b.query<Trend, Entity>({
      query: (s) => ({ ...s, path: `/trends/${encodeURIComponent(s.id)}` }),
      providesTags: (_, __, s) => [{ type: "Workspace", id: tag(s) }],
    }),
    evidence: b.query<
      Page<InsightItem | FrozenInsight>,
      Entity & { kind: "trends" | "drafts" | "competitors"; revision?: number }
    >({
      query: (s) => ({
        ...s,
        path: path(
          `/${s.kind}/${encodeURIComponent(s.id)}/${s.kind === "drafts" ? "evidence" : "items"}`,
          s.query,
        ),
      }),
      providesTags: (_, __, s) => pageTags("Items", s),
    }),
    supportingSources: b.query<InsightItem[], Entity & { revision?: number }>({
      async queryFn(scope, _api, _extra, request) {
        const items: InsightItem[] = [];
        const seen = new Set<string>();
        let cursor: string | null | undefined;
        do {
          const result = await request({
            ...scope,
            path: path(
              `/trends/${encodeURIComponent(scope.id)}/items`,
              `limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
            ),
          });
          if (result.error) return { error: result.error };
          const page = result.data as Page<InsightItem>;
          items.push(...page.items);
          cursor = page.next_cursor;
          if (cursor && seen.has(cursor))
            return {
              error: {
                status: 500,
                code: "pagination_failed",
                message: "Could not load all supporting sources. Try again.",
              },
            };
          if (cursor) seen.add(cursor);
        } while (cursor);
        return { data: items };
      },
      providesTags: (_, __, s) => [{ type: "Items", id: tag(s) }],
    }),
    competitors: b.query<Page<Competitor>, Query>({
      query: (s) => ({ ...s, path: path("/competitors", s.query) }),
      providesTags: (_, __, s) => pageTags("Workspace", s),
    }),
    competitor: b.query<Competitor, Entity>({
      query: (s) => ({
        ...s,
        path: `/competitors/${encodeURIComponent(s.id)}`,
      }),
      providesTags: (_, __, s) => [{ type: "Workspace", id: tag(s) }],
    }),
    subjects: b.query<Page<Subject>, Query>({
      query: (s) => ({ ...s, path: path("/subjects", s.query) }),
      providesTags: (_, __, s) => pageTags("Workspace", s),
    }),
    subjectOptions: b.query<Subject[], Scope>({
      async queryFn(scope, _api, _extra, request) {
        const items: Subject[] = [];
        let cursor: string | null | undefined;
        do {
          const result = await request({
            ...scope,
            path: path(
              "/subjects",
              `limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
            ),
          });
          if (result.error) return { error: result.error };
          const page = result.data as Page<Subject>;
          items.push(...page.items);
          cursor = page.next_cursor;
        } while (cursor);
        return { data: items };
      },
      providesTags: (_, __, s) => [{ type: "Workspace", id: tag(s) }],
    }),
    sources: b.query<SourcePage, Query>({
      query: (s) => ({ ...s, path: path("/sources", s.query) }),
      providesTags: (_, __, s) => pageTags("Workspace", s),
    }),
    run: b.query<Run, Entity>({
      query: (s) => ({ ...s, path: `/runs/${encodeURIComponent(s.id)}` }),
      providesTags: (run, _, s) => run?.recovery ? [{ type: "Workspace", id: tag(s) }] : [],
    }),
    preparationRuns: b.query<Page<Run>, Scope>({
      async queryFn(scope, _api, _extra, request) {
        const items = new Map<string, Run>();
        const seen = new Set<string>();
        let first: Page<Run> | undefined;
        let cursor: string | null | undefined;
        do {
          const result = await request({
            ...scope,
            path: path("/preparation/runs", `limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`),
          });
          if (result.error) return { error: result.error };
          const page = result.data as Page<Run>;
          first ??= page;
          for (const run of page.items) items.set(run.id, run);
          cursor = page.next_cursor;
          if (cursor && seen.has(cursor))
            return { error: { status: 500, code: "pagination_failed", message: "Could not load all monitoring requests. Try again." } };
          if (cursor) seen.add(cursor);
        } while (cursor);
        return { data: { ...first!, items: [...items.values()], next_cursor: null } };
      },
      providesTags: (_, __, s) => [{ type: "Workspace", id: tag(s) }],
    }),
    draft: b.query<Draft, Entity>({
      query: (s) => ({ ...s, path: `/drafts/${encodeURIComponent(s.id)}` }),
      providesTags: (_, __, s) => [{ type: "Draft", id: `${tag(s)}:${s.id}` }],
    }),
    createDraft: b.mutation<Draft, Scope & { body: DraftCreate; key: string }>({
      query: (s) => ({ ...s, path: "/drafts", method: "POST" }),
    }),
    saveDraft: b.mutation<Draft, Entity & { body: DraftPatch; key: string }>({
      query: (s) => ({ ...s, path: `/drafts/${s.id}`, method: "PATCH" }),
      async onQueryStarted(
        { clientId, actorId, preview, tutorial, id },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            insightsApi.util.updateQueryData(
              "draft",
              {
                clientId,
                actorId,
                ...(preview ? { preview } : {}),
                ...(tutorial != null ? { tutorial } : {}),
                id,
              },
              () => data,
            ),
          );
        } catch {
          /* The editor retains unsaved text and displays the mutation error. */
        }
      },
    }),
    generate: b.mutation<
      Run,
      Entity & { body: GenerationRequest; key: string }
    >({
      query: (s) => ({
        ...s,
        path: `/drafts/${s.id}/generations`,
        method: "POST",
      }),
    }),
    generation: b.query<Run, Entity & { jobId: string }>({
      query: (s) => ({ ...s, path: `/drafts/${s.id}/generations/${s.jobId}` }),
    }),
    review: b.mutation<
      ReviewState,
      Entity & { body: ReviewWrite; key: string }
    >({
      query: (s) => ({
        ...s,
        path: `/items/${s.id}/review`,
        method: "PUT",
      }),
      invalidatesTags: (_, __, s) => [{ type: "Items", id: tag(s) }],
    }),
    bookmark: b.mutation<
      BookmarkState,
      Entity & { saved: boolean; body: BookmarkWrite; key: string }
    >({
      query: (s) => ({
        ...s,
        path: `/items/${s.id}/bookmark`,
        method: s.saved ? "PUT" : "DELETE",
      }),
      invalidatesTags: (_, __, s) => [{ type: "Items", id: tag(s) }],
    }),
    write: b.mutation<unknown, Request>({
      query: (s) => s,
      async onQueryStarted(s, { dispatch, queryFulfilled }) {
        const match = /^\/drafts\/([^/]+)\/generations\/[^/]+\/apply$/.exec(
          s.path,
        );
        if (!match) return;
        try {
          const { data } = await queryFulfilled;
          dispatch(
            insightsApi.util.updateQueryData(
              "draft",
              {
                clientId: s.clientId,
                actorId: s.actorId,
                ...(s.preview ? { preview: true } : {}),
                ...(s.tutorial != null ? { tutorial: s.tutorial } : {}),
                id: match[1],
              },
              () => data as Draft,
            ),
          );
        } catch {
          /* Keep the current draft on replacement failure. */
        }
      },
      invalidatesTags: (_, error, s) =>
        error
          ? []
          : [
              { type: "Workspace", id: tag(s) },
              { type: "Items", id: tag(s) },
            ],
    }),
  }),
});
export const readOnlyCapabilities: Capabilities = {
  can_manage: false,
  can_generate: false,
  can_create_social_prompt: false,
};
